import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import {
  LayoutGrid, Users, Home, CalendarCheck, MessageSquare, LifeBuoy, Handshake,
  Loader2, Search, Mail, Phone, Trash2, CheckCircle2, XCircle, ExternalLink, Star,
} from "lucide-react";

const TABS = [
  { k: "overview", l: "Vue d'ensemble", Icon: LayoutGrid },
  { k: "users", l: "Utilisateurs", Icon: Users },
  { k: "listings", l: "Annonces", Icon: Home },
  { k: "bookings", l: "Réservations", Icon: CalendarCheck },
  { k: "messages", l: "Messagerie", Icon: MessageSquare },
  { k: "partners", l: "Partenaires", Icon: Handshake },
  { k: "support", l: "Support", Icon: LifeBuoy },
];

export default function AdminDashboardPage() {
  const { user } = useAuth();
  const [params, setParams] = useSearchParams();
  const tab = params.get("tab") || "overview";
  const setTab = (k) => setParams({ tab: k });

  if (!user) return null;
  if (user.role !== "admin") {
    return (
      <div className="pt-32 text-center text-neutral-500">
        Accès réservé aux administrateurs.
      </div>
    );
  }

  return (
    <div className="pt-24 sm:pt-28 min-h-screen bg-[#FAFAFA]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 grid grid-cols-1 lg:grid-cols-[260px_1fr] gap-10">
        {/* Sidebar */}
        <aside className="lg:sticky lg:top-28 h-fit">
          <div className="text-[10px] uppercase tracking-[0.3em] text-neutral-500 font-bold mb-3">Admin MSB</div>
          <h1 className="font-display text-2xl font-bold mb-6 leading-tight">Tableau de bord</h1>
          <nav className="space-y-1">
            {TABS.map(({ k, l, Icon }) => (
              <button
                key={k}
                data-testid={`adm-nav-${k}`}
                onClick={() => setTab(k)}
                className={`w-full flex items-center gap-3 px-3 py-2.5 text-sm font-semibold transition-colors border-l-2 ${
                  tab === k ? "bg-white border-[#0A0A0A] text-[#0A0A0A]" : "border-transparent text-neutral-600 hover:text-[#0A0A0A]"
                }`}
              >
                <Icon className="w-4 h-4" /> {l}
              </button>
            ))}
          </nav>
        </aside>

        {/* Main */}
        <main className="min-w-0">
          {tab === "overview" && <Overview />}
          {tab === "users" && <UsersPanel />}
          {tab === "listings" && <ListingsPanel />}
          {tab === "bookings" && <BookingsPanel />}
          {tab === "messages" && <MessagesPanel />}
          {tab === "partners" && <PartnersPanel />}
          {tab === "support" && <SupportPanel />}
        </main>
      </div>
    </div>
  );
}

/* ====================== OVERVIEW ====================== */
function Overview() {
  const [stats, setStats] = useState(null);
  useEffect(() => { api.get("/admin/stats").then(({ data }) => setStats(data)); }, []);
  if (!stats) return <Loader />;
  const Cards = [
    { l: "Utilisateurs", n: stats.users.total, sub: `${stats.users.owners} hôtes · ${stats.users.travelers} voyageurs`, link: "users" },
    { l: "Annonces", n: stats.listings.total, sub: `${stats.listings.published} publiées`, link: "listings" },
    { l: "Réservations", n: stats.bookings.total, sub: `${stats.bookings.paid} payées · ${stats.bookings.gmv}€ GMV`, link: "bookings" },
    { l: "Commission MSB", n: `${stats.bookings.commission}€`, sub: "Sur réservations payées", link: "bookings" },
    { l: "Partenaires en attente", n: stats.partners.pending, sub: `${stats.partners.approved} approuvés`, link: "partners", urgent: stats.partners.pending > 0 },
    { l: "Messages support", n: stats.support.new, sub: "Non lus", link: "support", urgent: stats.support.new > 0 },
  ];
  return (
    <div>
      <h2 className="font-display text-4xl font-bold tracking-tight mb-8">Pilotage</h2>
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
        {Cards.map((c, i) => (
          <div key={i} data-testid={`adm-stat-${c.link}`} className={`p-5 bg-white border ${c.urgent ? "border-[#C9A14A] bg-[#FFFBEA]" : "border-black/10"}`}>
            <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-neutral-500">{c.l}</div>
            <div className="font-display text-3xl font-bold mt-2 leading-none">{c.n}</div>
            <div className="text-xs text-neutral-600 mt-2">{c.sub}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ====================== USERS ====================== */
function UsersPanel() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [role, setRole] = useState("");
  const [q, setQ] = useState("");
  const [selected, setSelected] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/users", { params: { role: role || undefined, q: q || undefined } });
      setItems(data);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [role]);

  const submit = (e) => { e.preventDefault(); load(); };

  const remove = async (id) => {
    if (!window.confirm("Supprimer définitivement ce compte ?")) return;
    try {
      await api.delete(`/admin/users/${id}`);
      toast.success("Compte supprimé");
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Erreur"); }
  };

  const suspend = async (u) => {
    try {
      await api.put(`/admin/users/${u.id}`, { suspended: !u.suspended });
      toast.success(u.suspended ? "Compte réactivé" : "Compte suspendu");
      load();
    } catch { toast.error("Erreur"); }
  };

  return (
    <div>
      <h2 className="font-display text-4xl font-bold tracking-tight mb-6">Utilisateurs</h2>
      <form onSubmit={submit} className="flex flex-wrap gap-2 mb-4 items-center">
        <select value={role} onChange={(e) => setRole(e.target.value)} className="text-sm border border-black/15 px-3 py-2 bg-white" data-testid="usr-role">
          <option value="">Tous les rôles</option>
          <option value="traveler">Voyageurs</option>
          <option value="owner">Hôtes</option>
          <option value="admin">Admins</option>
        </select>
        <div className="flex-1 min-w-[200px] flex border border-black/15 bg-white">
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Rechercher (nom, email)" className="flex-1 px-3 py-2 text-sm outline-none" data-testid="usr-search-input" />
          <button type="submit" className="px-3 bg-[#0A0A0A] text-white" data-testid="usr-search-btn"><Search className="w-4 h-4" /></button>
        </div>
      </form>
      {loading ? <Loader /> : (
        <div className="bg-white border border-black/10 divide-y divide-black/5">
          {items.map((u) => (
            <div key={u.id} className="p-4 flex items-center gap-4 flex-wrap" data-testid={`usr-row-${u.id}`}>
              <div className="w-10 h-10 bg-[#0A0A0A] text-white flex items-center justify-center font-bold shrink-0 rounded-full">{u.name?.[0]?.toUpperCase()}</div>
              <div className="flex-1 min-w-[200px]">
                <div className="font-semibold flex items-center gap-2 flex-wrap">
                  {u.name}
                  <span className="text-[10px] uppercase tracking-[0.18em] font-bold bg-neutral-100 px-2 py-0.5">{u.role}</span>
                  {u.suspended && <span className="text-[10px] uppercase tracking-[0.18em] font-bold bg-[#991B1B] text-white px-2 py-0.5">Suspendu</span>}
                  {u.stripe_charges_enabled && <span className="text-[10px] uppercase tracking-[0.18em] font-bold bg-[#166534] text-white px-2 py-0.5">Stripe OK</span>}
                </div>
                <div className="text-xs text-neutral-600 mt-1">{u.email}</div>
                <div className="text-xs text-neutral-500 mt-1">{u.listings_count} annonces · {u.bookings_count} réservations</div>
              </div>
              <div className="flex items-center gap-1">
                <button onClick={() => setSelected(u.id)} data-testid={`usr-voir-${u.id}`} className="px-3 py-1.5 text-xs uppercase tracking-[0.15em] font-bold border border-black/15">Voir</button>
                <button onClick={() => suspend(u)} data-testid={`usr-suspend-${u.id}`} className="px-3 py-1.5 text-xs uppercase tracking-[0.15em] font-bold border border-[#C9A14A] text-[#7A1535]">{u.suspended ? "Réactiver" : "Suspendre"}</button>
                <button onClick={() => remove(u.id)} data-testid={`usr-delete-${u.id}`} className="p-2 border border-black/15 hover:border-[#991B1B] hover:text-[#991B1B]"><Trash2 className="w-4 h-4" /></button>
              </div>
            </div>
          ))}
          {items.length === 0 && <div className="p-6 text-center text-neutral-500 text-sm">Aucun utilisateur</div>}
        </div>
      )}
      {selected && <UserDetailModal id={selected} onClose={() => setSelected(null)} />}
    </div>
  );
}

function UserDetailModal({ id, onClose }) {
  const [u, setU] = useState(null);
  useEffect(() => { api.get(`/admin/users/${id}`).then(({ data }) => setU(data)); }, [id]);
  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className="bg-white max-w-3xl w-full max-h-[85vh] overflow-y-auto" data-testid="usr-modal">
        <div className="p-6 border-b border-black/10 flex items-center justify-between sticky top-0 bg-white">
          <div>
            <div className="font-display text-2xl font-bold">{u?.name || "…"}</div>
            <div className="text-xs text-neutral-500">{u?.email}</div>
          </div>
          <button onClick={onClose} className="text-2xl leading-none">×</button>
        </div>
        {!u ? <div className="p-12"><Loader /></div> : (
          <div className="p-6 space-y-6">
            <div className="grid grid-cols-2 gap-3 text-sm">
              <Info l="Rôle" v={u.role} />
              <Info l="Email vérifié" v={u.email_verified ? "Oui" : "Non"} />
              <Info l="Stripe Connect" v={u.stripe_charges_enabled ? "Actif" : "Non configuré"} />
              <Info l="Inscrit le" v={u.created_at?.split("T")[0]} />
            </div>
            {u.listings?.length > 0 && (
              <div>
                <div className="text-[10px] uppercase tracking-[0.2em] font-bold mb-2">Annonces ({u.listings.length})</div>
                {u.listings.map((l) => (
                  <Link to={`/listings/${l.id}`} key={l.id} className="block border border-black/10 px-3 py-2 mb-2 text-sm hover:border-[#0A0A0A]">{l.title} <span className="text-xs text-neutral-500">· {l.city}</span></Link>
                ))}
              </div>
            )}
            {u.bookings_as_guest?.length > 0 && (
              <div>
                <div className="text-[10px] uppercase tracking-[0.2em] font-bold mb-2">Réservations effectuées ({u.bookings_as_guest.length})</div>
                {u.bookings_as_guest.slice(0, 5).map((b) => (
                  <div key={b.id} className="border border-black/10 px-3 py-2 mb-2 text-sm">
                    {b.listing_title} — {b.check_in} → {b.check_out} · <strong>{b.amount}€</strong> · <span className="uppercase text-[10px] tracking-[0.15em] font-bold">{b.status}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function Info({ l, v }) {
  return (
    <div className="border border-black/10 p-3">
      <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-neutral-500">{l}</div>
      <div className="font-semibold text-sm mt-1">{v}</div>
    </div>
  );
}

/* ====================== LISTINGS ====================== */
function ListingsPanel() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState(""); // "" all | "true" published | "false" draft

  const load = async () => {
    setLoading(true);
    const params = filter === "" ? {} : { published: filter === "true" };
    const { data } = await api.get("/admin/listings", { params });
    setItems(data);
    setLoading(false);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter]);

  const toggle = async (l) => {
    try {
      const url = l.published ? `/listings/${l.id}/unpublish` : `/listings/${l.id}/publish`;
      await api.post(url);
      toast.success("Statut mis à jour");
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Erreur"); }
  };

  return (
    <div>
      <h2 className="font-display text-4xl font-bold tracking-tight mb-6">Annonces</h2>
      <div className="flex gap-2 mb-4">
        {[
          { v: "", l: "Toutes" },
          { v: "true", l: "Publiées" },
          { v: "false", l: "Brouillons" },
        ].map((f) => (
          <button key={f.v} onClick={() => setFilter(f.v)} data-testid={`lst-filter-${f.v || "all"}`} className={`px-3 py-1.5 text-xs uppercase tracking-[0.15em] font-bold border ${filter === f.v ? "bg-[#0A0A0A] text-white border-[#0A0A0A]" : "bg-white border-black/15"}`}>{f.l}</button>
        ))}
      </div>
      {loading ? <Loader /> : (
        <div className="bg-white border border-black/10 divide-y divide-black/5">
          {items.map((l) => (
            <div key={l.id} className="p-4 flex items-center gap-4 flex-wrap" data-testid={`adm-lst-${l.id}`}>
              <div className="flex-1 min-w-[200px]">
                <Link to={`/listings/${l.id}`} className="font-semibold hover:underline">{l.title}</Link>
                <div className="text-xs text-neutral-600 mt-1">{l.city} · {l.region} · {l.price_per_night}€/nuit</div>
                <div className="text-xs text-neutral-500 mt-1">Par {l.owner?.name || "—"} · {l.owner?.email}</div>
              </div>
              <span className={`text-[10px] uppercase tracking-[0.18em] font-bold px-2 py-1 ${l.published ? "bg-[#166534] text-white" : "bg-[#7A1535] text-white"}`}>
                {l.published ? "Publiée" : "Brouillon"}
              </span>
              <button onClick={() => toggle(l)} data-testid={`lst-toggle-${l.id}`} className="px-3 py-1.5 text-xs uppercase tracking-[0.15em] font-bold border border-black/15">
                {l.published ? "Dépublier" : "Publier"}
              </button>
              <Link to={`/listings/${l.id}`} className="p-2 border border-black/15 hover:border-[#0A0A0A]"><ExternalLink className="w-4 h-4" /></Link>
            </div>
          ))}
          {items.length === 0 && <div className="p-6 text-center text-neutral-500 text-sm">Aucune annonce</div>}
        </div>
      )}
    </div>
  );
}

/* ====================== BOOKINGS ====================== */
function BookingsPanel() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState("");
  useEffect(() => {
    setLoading(true);
    api.get("/admin/bookings", { params: status ? { status } : {} })
      .then(({ data }) => setItems(data)).finally(() => setLoading(false));
  }, [status]);

  return (
    <div>
      <h2 className="font-display text-4xl font-bold tracking-tight mb-6">Réservations</h2>
      <div className="flex gap-2 mb-4">
        {[{ v: "", l: "Toutes" }, { v: "pending", l: "En attente" }, { v: "confirmed", l: "Confirmées" }, { v: "cancelled", l: "Annulées" }].map((f) => (
          <button key={f.v} onClick={() => setStatus(f.v)} className={`px-3 py-1.5 text-xs uppercase tracking-[0.15em] font-bold border ${status === f.v ? "bg-[#0A0A0A] text-white border-[#0A0A0A]" : "bg-white border-black/15"}`}>{f.l}</button>
        ))}
      </div>
      {loading ? <Loader /> : (
        <div className="bg-white border border-black/10 divide-y divide-black/5">
          {items.map((b) => (
            <div key={b.id} className="p-4 flex flex-wrap items-center gap-4">
              <div className="flex-1 min-w-[240px]">
                <div className="font-semibold">{b.listing_title}</div>
                <div className="text-xs text-neutral-600 mt-1">{b.check_in} → {b.check_out} · {b.guests} voyageurs · {b.nights} nuits</div>
                <div className="text-xs text-neutral-500 mt-1">{b.guest_name} · {b.guest_email}</div>
              </div>
              <div className="text-right">
                <div className="font-display text-xl font-bold">{b.amount}€</div>
                <div className="text-[10px] uppercase tracking-[0.18em] font-bold mt-1">{b.status} · {b.payment_status}</div>
              </div>
            </div>
          ))}
          {items.length === 0 && <div className="p-6 text-center text-neutral-500 text-sm">Aucune réservation</div>}
        </div>
      )}
    </div>
  );
}

/* ====================== MESSAGES ====================== */
function MessagesPanel() {
  const [threads, setThreads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  useEffect(() => {
    api.get("/admin/messages/threads").then(({ data }) => setThreads(data)).finally(() => setLoading(false));
  }, []);

  return (
    <div>
      <h2 className="font-display text-4xl font-bold tracking-tight mb-6">Messagerie (lecture seule)</h2>
      {loading ? <Loader /> : (
        <div className="grid grid-cols-1 lg:grid-cols-[320px_1fr] gap-4">
          <div className="bg-white border border-black/10 divide-y divide-black/5 max-h-[70vh] overflow-y-auto">
            {threads.map((t) => {
              const parts = (t.participants_info || []).map((p) => p?.name || "?").join(" ↔ ");
              const last = t.last_message?.content?.slice(0, 60) || "—";
              return (
                <button key={t.id} onClick={() => setSelected(t.id)} className={`w-full text-left p-3 hover:bg-neutral-50 ${selected === t.id ? "bg-neutral-100" : ""}`} data-testid={`msg-thread-${t.id}`}>
                  <div className="font-semibold text-sm truncate">{parts}</div>
                  <div className="text-xs text-neutral-500 mt-1 truncate">{last}</div>
                  <div className="text-[10px] uppercase tracking-[0.15em] text-neutral-400 mt-1">{t.message_count} messages</div>
                </button>
              );
            })}
            {threads.length === 0 && <div className="p-6 text-center text-neutral-500 text-sm">Aucune conversation</div>}
          </div>
          <ThreadDetail id={selected} />
        </div>
      )}
    </div>
  );
}

function ThreadDetail({ id }) {
  const [t, setT] = useState(null);
  useEffect(() => {
    if (!id) { setT(null); return; }
    api.get(`/admin/messages/threads/${id}`).then(({ data }) => setT(data));
  }, [id]);
  if (!id) return <div className="bg-white border border-black/10 p-12 text-center text-neutral-500 text-sm">Sélectionnez une conversation à gauche</div>;
  if (!t) return <Loader />;
  return (
    <div className="bg-white border border-black/10 p-5 max-h-[70vh] overflow-y-auto">
      <div className="border-b border-black/10 pb-3 mb-4">
        <div className="font-display text-lg font-bold">{(t.participants_info || []).map((p) => `${p?.name} (${p?.role})`).join(" ↔ ")}</div>
        <div className="text-xs text-neutral-500">{(t.messages || []).length} messages</div>
      </div>
      <div className="space-y-3">
        {(t.messages || []).map((m) => {
          const sender = t.users_map?.[m.sender_id];
          return (
            <div key={m.id} className="border border-black/10 p-3">
              <div className="flex items-baseline gap-2 text-xs">
                <strong>{sender?.name || "?"}</strong>
                <span className="text-neutral-500 text-[10px] uppercase tracking-[0.15em]">{sender?.role}</span>
                <span className="text-neutral-400 ml-auto">{m.created_at?.slice(0, 16).replace("T", " ")}</span>
              </div>
              <div className="text-sm mt-1 whitespace-pre-wrap">{m.content}</div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* ====================== PARTNERS ====================== */
function PartnersPanel() {
  const [tab, setTab] = useState("pending");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [chatting, setChatting] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/partners", { params: { status: tab } });
      setItems(data);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [tab]);

  const decide = async (id, action, reason) => {
    try {
      await api.post(`/admin/partners/${id}/${action}`, { reason });
      toast.success(action === "approve" ? "Partenaire approuvé" : "Refusé");
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Erreur"); }
  };

  const del = async (p) => {
    if (!window.confirm(`Supprimer définitivement « ${p.company_name} » ? Les annonces liées seront détachées de ce partenaire.`)) return;
    try {
      await api.delete(`/admin/partners/${p.id}`);
      toast.success("Partenaire supprimé");
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Erreur"); }
  };

  const resolveLogo = (p) => {
    if (!p) return null;
    return p.startsWith("http") ? p : `${process.env.REACT_APP_BACKEND_URL}/api/files/${p}`;
  };

  return (
    <div>
      <h2 className="font-display text-4xl font-bold tracking-tight mb-6">Partenaires (conciergerie · ménage)</h2>
      <div className="flex gap-2 mb-4">
        {[{ v: "pending", l: "En attente" }, { v: "approved", l: "Approuvés" }, { v: "rejected", l: "Refusés" }].map((f) => (
          <button key={f.v} onClick={() => setTab(f.v)} className={`px-3 py-1.5 text-xs uppercase tracking-[0.15em] font-bold border ${tab === f.v ? "bg-[#0A0A0A] text-white border-[#0A0A0A]" : "bg-white border-black/15"}`}>{f.l}</button>
        ))}
      </div>
      {loading ? <Loader /> : items.length === 0 ? (
        <div className="bg-white border border-black/10 p-8 text-center text-neutral-500 text-sm">Aucun dossier</div>
      ) : (
        <div className="space-y-3">
          {items.map((p) => (
            <div key={p.id} className="bg-white border border-black/10 p-5" data-testid={`adm-prtn-row-${p.id}`}>
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div className="flex-1 min-w-[220px] flex items-start gap-4">
                  {/* Logo */}
                  <div className="w-16 h-16 border border-black/10 bg-neutral-50 flex items-center justify-center overflow-hidden shrink-0">
                    {p.logo ? (
                      <img src={resolveLogo(p.logo)} alt={p.company_name} className="w-full h-full object-cover" data-testid={`adm-prtn-logo-${p.id}`} />
                    ) : (
                      <span className="text-[9px] uppercase tracking-[0.18em] text-neutral-400">Pas de logo</span>
                    )}
                  </div>
                  <div className="flex-1">
                    <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-neutral-500">{p.partner_type === "conciergerie" ? "Conciergerie" : "Ménage"} · {p.legal_status}</div>
                    <div className="font-display text-xl font-bold mt-1">{p.company_name}</div>
                    <div className="text-sm text-neutral-700 mt-1">{p.contact_name}</div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-y-1 mt-3 text-xs text-neutral-700">
                      <div className="flex items-center gap-1.5"><Mail className="w-3 h-3" /> {p.email}</div>
                      <div className="flex items-center gap-1.5"><Phone className="w-3 h-3" /> {p.phone}</div>
                    </div>
                    {p.default_revenue_share_percent != null && (
                      <div className="mt-2 inline-flex items-center gap-1 text-[11px] uppercase tracking-[0.15em] font-bold bg-[#7A1535] text-white px-2 py-0.5">
                        Part par défaut : {p.default_revenue_share_percent}%
                      </div>
                    )}
                    <p className="text-sm text-neutral-600 mt-2 line-clamp-3">{p.description}</p>
                  </div>
                </div>
                <div className="flex flex-col gap-2 w-full sm:w-auto">
                  {tab === "pending" && (
                    <>
                      <button onClick={() => decide(p.id, "approve")} data-testid={`adm-pap-${p.id}`} className="bg-[#166534] text-white px-4 py-2 font-semibold flex items-center gap-2"><CheckCircle2 className="w-4 h-4" /> Approuver</button>
                      <button onClick={() => decide(p.id, "reject", window.prompt("Motif ?") || undefined)} data-testid={`adm-prj-${p.id}`} className="border-2 border-[#991B1B] text-[#991B1B] px-4 py-2 font-semibold flex items-center gap-2"><XCircle className="w-4 h-4" /> Refuser</button>
                    </>
                  )}
                  <button onClick={() => setEditing(p)} data-testid={`adm-pedit-${p.id}`} className="border border-black/15 text-[#0A0A0A] px-4 py-2 font-semibold text-sm hover:bg-neutral-50">Modifier</button>
                  <button onClick={() => setChatting(p)} data-testid={`adm-pchat-${p.id}`} disabled={!p.user_id} className="bg-[#0A0A0A] text-white px-4 py-2 font-semibold text-sm flex items-center gap-2 hover:bg-[#1B2A49] disabled:opacity-40 disabled:cursor-not-allowed">
                    <MessageSquare className="w-4 h-4" /> {p.user_id ? "Contacter" : "Pas de compte lié"}
                  </button>
                  <button onClick={() => del(p)} data-testid={`adm-pdel-${p.id}`} className="border-2 border-[#991B1B] text-[#991B1B] px-4 py-2 font-semibold text-sm flex items-center justify-center gap-2 hover:bg-[#991B1B] hover:text-white transition-colors">
                    <Trash2 className="w-4 h-4" /> Supprimer
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {editing && (
        <PartnerEditModal
          partner={editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}
      {chatting && (
        <PartnerChatModal
          partner={chatting}
          onClose={() => setChatting(null)}
        />
      )}
    </div>
  );
}

function PartnerEditModal({ partner, onClose, onSaved }) {
  const [form, setForm] = useState({
    company_name: partner.company_name || "",
    contact_name: partner.contact_name || "",
    email: partner.email || "",
    phone: partner.phone || "",
    description: partner.description || "",
    price_range: partner.price_range || "",
    website: partner.website || "",
    instagram: partner.instagram || "",
    logo: partner.logo || "",
    insurance: !!partner.insurance,
    partner_type: partner.partner_type || "menage",
    legal_status: partner.legal_status || "auto-entrepreneur",
    services: Array.isArray(partner.services) ? partner.services : [],
    regions: Array.isArray(partner.regions) ? partner.regions : [],
    cities_str: (partner.cities || []).join(", "),
    default_revenue_share_percent:
      partner.default_revenue_share_percent != null ? partner.default_revenue_share_percent : 15,
  });
  const [saving, setSaving] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));
  const share = Number(form.default_revenue_share_percent);

  const onLogoChange = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploadingLogo(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      set("logo", data.path);
      toast.success("Logo téléchargé");
    } catch (err) {
      toast.error("Échec de l'upload");
    } finally {
      setUploadingLogo(false);
      e.target.value = "";
    }
  };

  const resolveLogo = (p) => (!p ? null : p.startsWith("http") ? p : `${process.env.REACT_APP_BACKEND_URL}/api/files/${p}`);

  const save = async () => {
    if (share < 0 || share > 100) {
      toast.error("Le pourcentage doit être entre 0 et 100");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        default_revenue_share_percent: share,
        price_range: form.price_range || null,
        website: form.website || null,
        instagram: form.instagram || null,
        logo: form.logo || null,
        services: form.services || [],
        regions: form.regions || [],
        cities: (form.cities_str || "").split(",").map((c) => c.trim()).filter(Boolean),
      };
      delete payload.cities_str;
      await api.patch(`/admin/partners/${partner.id}`, payload);
      toast.success("Partenaire mis à jour");
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/60 z-[60] flex items-center justify-center p-4 overflow-y-auto" onClick={onClose}>
      <div className="bg-white max-w-2xl w-full p-6 sm:p-8 my-8" onClick={(e) => e.stopPropagation()} data-testid="adm-partner-edit-modal">
        <div className="text-[10px] uppercase tracking-[0.3em] font-bold text-[#7A1535] mb-2">Modifier la fiche partenaire</div>
        <div className="font-display text-2xl font-bold leading-tight mb-6">{partner.company_name}</div>

        {/* Logo */}
        <div className="mb-5">
          <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-neutral-500 mb-2">Logo de l'entreprise</div>
          <div className="flex items-center gap-4">
            <div className="w-24 h-24 border border-black/15 bg-neutral-50 flex items-center justify-center overflow-hidden">
              {form.logo ? (
                <img src={resolveLogo(form.logo)} alt="logo" className="w-full h-full object-cover" data-testid="adm-logo-preview" />
              ) : (
                <span className="text-[10px] uppercase tracking-[0.18em] text-neutral-400">Aperçu</span>
              )}
            </div>
            <label className="cursor-pointer inline-flex items-center gap-2 border border-black/15 px-4 py-2 text-sm font-semibold hover:bg-neutral-50" data-testid="adm-logo-upload">
              <input type="file" accept="image/*" onChange={onLogoChange} className="hidden" />
              {uploadingLogo ? <><Loader2 className="w-4 h-4 animate-spin" /> Envoi…</> : "Choisir un fichier"}
            </label>
            {form.logo && (
              <button type="button" onClick={() => set("logo", "")} className="text-xs underline text-neutral-500 hover:text-[#9A4D1F]" data-testid="adm-logo-remove">
                Retirer
              </button>
            )}
          </div>
        </div>

        {/* Type & Statut juridique */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-5">
          <div data-testid="adm-ed-type-block">
            <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-neutral-500 mb-2">Type de partenaire</div>
            <div className="grid grid-cols-2 gap-2">
              {[
                { v: "menage", l: "Ménage" },
                { v: "conciergerie", l: "Conciergerie" },
              ].map((t) => (
                <button
                  type="button"
                  key={t.v}
                  onClick={() => set("partner_type", t.v)}
                  data-testid={`adm-type-${t.v}`}
                  className={`px-3 py-2 border text-sm font-semibold transition-colors ${form.partner_type === t.v ? "bg-[#0A0A0A] text-white border-[#0A0A0A]" : "bg-white border-black/15 hover:border-[#0A0A0A]"}`}
                >
                  {t.l}
                </button>
              ))}
            </div>
          </div>
          <div data-testid="adm-ed-legal-block">
            <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-neutral-500 mb-2">Statut juridique</div>
            <select
              value={form.legal_status}
              onChange={(e) => set("legal_status", e.target.value)}
              className="w-full border border-black/15 px-3 py-2 text-sm bg-white"
              data-testid="adm-ed-legal-select"
            >
              <option value="auto-entrepreneur">Auto-entrepreneur</option>
              <option value="cesu">CESU</option>
              <option value="micro-entreprise">Micro-entreprise</option>
              <option value="sasu">SASU</option>
              <option value="sarl">SARL</option>
              <option value="association">Association</option>
              <option value="autre">Autre</option>
            </select>
          </div>
        </div>

        {/* Services cocher */}
        <div className="mb-5" data-testid="adm-ed-services-block">
          <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-neutral-500 mb-2">Services proposés</div>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
            {[
              { v: "checkin", l: "Accueil voyageurs" },
              { v: "key_handover", l: "Remise des clés" },
              { v: "cleaning", l: "Ménage" },
              { v: "linen", l: "Linge" },
              { v: "photo", l: "Reportage photo" },
              { v: "maintenance", l: "Maintenance" },
              { v: "conciergerie_full", l: "Conciergerie clé en main" },
              { v: "garden", l: "Jardin / piscine" },
            ].map((s) => {
              const checked = (form.services || []).includes(s.v);
              return (
                <label key={s.v} className={`flex items-center gap-2 px-2 py-1.5 border text-xs font-semibold cursor-pointer transition-colors ${checked ? "bg-[#F5F2EC] border-[#0A0A0A]" : "bg-white border-black/10 hover:border-[#0A0A0A]"}`} data-testid={`adm-srv-${s.v}`}>
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={(e) => {
                      const next = e.target.checked
                        ? [...(form.services || []), s.v]
                        : (form.services || []).filter((x) => x !== s.v);
                      set("services", next);
                    }}
                    className="accent-[#0A0A0A]"
                  />
                  {s.l}
                </label>
              );
            })}
          </div>
        </div>

        {/* Secteur d'intervention */}
        <div className="mb-5" data-testid="adm-ed-sector-block">
          <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-neutral-500 mb-2">Secteur d'intervention</div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-3">
            {["Finistère", "Côtes-d'Armor", "Morbihan", "Ille-et-Vilaine"].map((r) => {
              const checked = (form.regions || []).includes(r);
              return (
                <button
                  type="button"
                  key={r}
                  onClick={() => {
                    const next = checked
                      ? (form.regions || []).filter((x) => x !== r)
                      : [...(form.regions || []), r];
                    set("regions", next);
                  }}
                  data-testid={`adm-region-${r}`}
                  className={`px-3 py-2 border text-xs font-semibold transition-colors ${checked ? "bg-[#0A0A0A] text-white border-[#0A0A0A]" : "bg-white border-black/15 hover:border-[#0A0A0A]"}`}
                >
                  {r}
                </button>
              );
            })}
          </div>
          <input
            value={form.cities_str}
            onChange={(e) => set("cities_str", e.target.value)}
            placeholder="Villes (séparées par des virgules) : Quimper, Brest, Concarneau…"
            className="w-full border border-black/15 px-3 py-2 text-sm"
            data-testid="adm-ed-cities-input"
          />
          <p className="text-[11px] text-neutral-500 mt-1">Les villes affinent le secteur affiché sur la fiche annuaire et l'inscription.</p>
        </div>

        {/* Identité */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
          <AdmField label="Nom de l'entreprise" testid="adm-ed-company">
            <input value={form.company_name} onChange={(e) => set("company_name", e.target.value)} className="ad-inp" />
          </AdmField>
          <AdmField label="Nom du contact" testid="adm-ed-contact">
            <input value={form.contact_name} onChange={(e) => set("contact_name", e.target.value)} className="ad-inp" />
          </AdmField>
          <AdmField label="Email" testid="adm-ed-email">
            <input type="email" value={form.email} onChange={(e) => set("email", e.target.value)} className="ad-inp" />
          </AdmField>
          <AdmField label="Téléphone" testid="adm-ed-phone">
            <input type="tel" value={form.phone} onChange={(e) => set("phone", e.target.value)} className="ad-inp" />
          </AdmField>
          <AdmField label="Site web" testid="adm-ed-website">
            <input type="url" value={form.website} onChange={(e) => set("website", e.target.value)} placeholder="https://…" className="ad-inp" />
          </AdmField>
          <AdmField label="Instagram" testid="adm-ed-instagram">
            <input value={form.instagram} onChange={(e) => set("instagram", e.target.value)} placeholder="@compte" className="ad-inp" />
          </AdmField>
        </div>

        <AdmField label="Description" testid="adm-ed-desc">
          <textarea rows={4} value={form.description} onChange={(e) => set("description", e.target.value)} className="ad-inp" />
        </AdmField>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-4">
          <AdmField label="Tarif indicatif" testid="adm-ed-price">
            <input value={form.price_range} onChange={(e) => set("price_range", e.target.value)} placeholder="Ex : 25–45€ / passage" className="ad-inp" />
          </AdmField>
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-neutral-500 mb-2">Commission par défaut</div>
            <div className="flex items-center gap-3">
              <input
                type="range" min={0} max={40} step={0.5}
                value={share}
                onChange={(e) => set("default_revenue_share_percent", Number(e.target.value))}
                className="flex-1 accent-[#7A1535]"
                data-testid="adm-share-slider"
              />
              <input
                type="number" min={0} max={100} step={0.5}
                value={share}
                onChange={(e) => set("default_revenue_share_percent", Number(e.target.value))}
                className="w-16 border border-black/15 px-2 py-1.5 text-right font-display text-base font-bold"
                data-testid="adm-share-input"
              />
              <span className="font-bold text-sm">%</span>
            </div>
          </div>
        </div>

        <label className="flex items-center gap-2 text-sm mt-5 mb-6">
          <input type="checkbox" checked={form.insurance} onChange={(e) => set("insurance", e.target.checked)} data-testid="adm-insurance-toggle" />
          <span>Assurance responsabilité civile professionnelle vérifiée</span>
        </label>

        <div className="flex justify-end gap-3 pt-4 border-t border-black/10">
          <button onClick={onClose} className="px-5 py-2 text-sm font-semibold border border-black/15">Annuler</button>
          <button onClick={save} disabled={saving} className="px-5 py-2 text-sm font-semibold bg-[#0A0A0A] text-white disabled:opacity-50" data-testid="adm-save-partner">
            {saving ? "Enregistrement…" : "Enregistrer"}
          </button>
        </div>
        <style>{`.ad-inp { width: 100%; border: 1px solid rgba(0,0,0,.15); padding: .55rem .75rem; outline: none; font-size: .875rem; background: white } .ad-inp:focus { border-color: #0A0A0A }`}</style>
      </div>
    </div>
  );
}

function AdmField({ label, testid, children }) {
  return (
    <label className="block" data-testid={testid}>
      <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-neutral-500 mb-1">{label}</div>
      {children}
    </label>
  );
}

function PartnerChatModal({ partner, onClose }) {
  const [content, setContent] = useState("");
  const [sending, setSending] = useState(false);

  const send = async () => {
    if (!content.trim()) {
      toast.error("Le message ne peut pas être vide");
      return;
    }
    setSending(true);
    try {
      await api.post(`/admin/partners/${partner.id}/contact`, {
        recipient_id: partner.user_id || "",
        content: content.trim(),
      });
      toast.success("Message envoyé au partenaire");
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    } finally { setSending(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/60 z-[60] flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white max-w-lg w-full p-7" onClick={(e) => e.stopPropagation()} data-testid="adm-partner-chat-modal">
        <div className="text-[10px] uppercase tracking-[0.3em] font-bold text-[#7A1535] mb-2">Contacter le partenaire</div>
        <div className="font-display text-2xl font-bold leading-tight mb-1">{partner.company_name}</div>
        <p className="text-xs text-neutral-500 mb-5">{partner.contact_name} · {partner.email}</p>

        <textarea
          rows={6}
          value={content}
          onChange={(e) => setContent(e.target.value)}
          placeholder="Tapez votre message — il atterrira dans la messagerie du partenaire."
          className="w-full border border-black/15 px-3 py-2 text-sm"
          data-testid="adm-chat-textarea"
        />

        <div className="flex justify-end gap-3 mt-4">
          <button onClick={onClose} className="px-5 py-2 text-sm font-semibold border border-black/15">Annuler</button>
          <button onClick={send} disabled={sending} className="px-5 py-2 text-sm font-semibold bg-[#0A0A0A] text-white disabled:opacity-50" data-testid="adm-send-chat">
            {sending ? "Envoi…" : "Envoyer le message"}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ====================== SUPPORT ====================== */
function SupportPanel() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => { api.get("/admin/support/messages").then(({ data }) => setItems(data)).finally(() => setLoading(false)); }, []);
  const TOPIC_LABELS = { booking: "Réservation", owner: "Hôte", payment: "Paiement", partner: "Partenaire", other: "Autre" };

  return (
    <div>
      <h2 className="font-display text-4xl font-bold tracking-tight mb-6">Messages support</h2>
      {loading ? <Loader /> : items.length === 0 ? (
        <div className="bg-white border border-black/10 p-8 text-center text-neutral-500 text-sm">Aucun message</div>
      ) : (
        <div className="space-y-3">
          {items.map((m) => (
            <div key={m.id} className="bg-white border border-black/10 p-5">
              <div className="flex items-start justify-between gap-3 mb-2 flex-wrap">
                <div>
                  <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-neutral-500">{TOPIC_LABELS[m.topic] || m.topic} · {m.created_at?.slice(0, 10)}</div>
                  <div className="font-display text-lg font-bold mt-1">{m.name}</div>
                  <div className="text-xs text-neutral-600 mt-1">
                    <a href={`mailto:${m.email}`} className="hover:underline">{m.email}</a>
                    {m.phone && <span> · <a href={`tel:${m.phone}`} className="hover:underline">{m.phone}</a></span>}
                  </div>
                </div>
                {m.user_id && <span className="text-[9px] uppercase tracking-[0.18em] font-bold bg-neutral-100 px-2 py-0.5">Utilisateur connecté</span>}
              </div>
              <p className="text-sm text-neutral-800 mt-3 whitespace-pre-wrap">{m.message}</p>
              <div className="mt-3 flex gap-2">
                <a href={`mailto:${m.email}?subject=Re: votre message à MSB`} className="text-xs font-bold underline">Répondre par email</a>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function Loader() {
  return <div className="py-12 text-center text-neutral-500"><Loader2 className="w-5 h-5 animate-spin inline mr-2" /> Chargement…</div>;
}
